import Box from '@mui/material/Box';
import React from 'react';
import { AnimateIn } from './animate-in';

/** Page header surfaces (Claude Design: ARC header dark / light) */
const HEADER_COLORS = {
  dark: { background: '#26292b', border: '#ffffff14', title: '#ffffff' },
  light: { background: '#e9e9e9', border: '#0000001a', title: '#15181b' },
};

/**
 * The page header under the App Bar: the page title, and the header content
 * (usually the impersonation search) on the right.
 *
 * 64px at md+. Below md the header content gets its own full-width row
 * under the title, as in the design's phone layout.
 *
 * @param routeTitle - The page's h1
 * @param headerContent - Right side at md+, its own row below md
 * @param wrapContentForMobile - Kept for existing pages; the content now
 *   always wraps below md
 * @param isDarkMode - Dark header (#26292b) or the light one (#e9e9e9)
 */
export function AppHeader(props: {
  routeTitle: string;
  headerContent?: React.ReactNode;
  wrapContentForMobile?: boolean;
  isDarkMode?: boolean;
}) {
  const colors = props.isDarkMode ? HEADER_COLORS.dark : HEADER_COLORS.light;
  const hasContent = Boolean(props.headerContent);

  return (
    <Box
      sx={{
        flex: 'none',
        position: 'relative',
        // Above the page, so the search panel opens over it
        zIndex: 4,
        backgroundColor: colors.background,
        borderBottom: `1px solid ${colors.border}`,
        letterSpacing: 'normal',
        display: 'flex',
        flexDirection: { xs: hasContent ? 'column' : 'row', md: 'row' },
        alignItems: { xs: hasContent ? 'stretch' : 'center', md: 'center' },
        gap: { xs: '10px', md: 2 },
        minHeight: { md: 64 },
        p: { xs: '12px 12px 12px 16px', md: '8px 12px 8px 48px' },
        boxSizing: 'border-box',
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <AnimateIn nofade="true">
          <Box
            component="h1"
            sx={{
              m: 0,
              fontSize: 20,
              fontWeight: 500,
              lineHeight: '32px',
              color: colors.title,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {props.routeTitle}
          </Box>
        </AnimateIn>
      </Box>

      {props.headerContent}
    </Box>
  );
}
